import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject, input, model } from '@angular/core';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { numberStepperBridgeStyles, numberStepperStyleId } from './number.stepper.theme';

/**
 * A bordered pill of three segments — decrement, the value, increment — for nudging a small integer
 * without opening a keyboard: an interval count, a repeat every N, a lead time in days.
 *
 * The value is the component's own state via `model`, so the parent binds it two-way and never has to
 * clamp. Every path in here funnels through `commit`, which is the single place that clamps to
 * `min`/`max`, so a bound value can never leave the range by way of this control. Values outside the
 * range that arrive from the parent are left untouched — clamping someone else's model on read would
 * silently rewrite data this component does not own.
 *
 * Arrow keys are bound on the host rather than on the buttons: the whole pill reads as one control, and
 * a user who has tabbed to either button expects Up/Down to work the same from both.
 */
@Component({
  selector: 'wb-number-stepper',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class.disabled]': '$disabled()',
    '(keydown.arrowup)': 'onArrowKey($event, 1)',
    '(keydown.arrowdown)': 'onArrowKey($event, -1)',
  },
  templateUrl: 'number.stepper.component.html',
  styleUrls: ['./number.stepper.component.less'],
})
export class NumberStepperComponent {

  private readonly document = inject(DOCUMENT);

  /** Current value. Two-way: the control writes clamped values back through it. */
  public readonly $value = model<number>(0, { alias: 'value' });

  /** Lower bound, inclusive. Omitted means unbounded below. */
  public readonly $min = input<number | null>(null, { alias: 'min' });

  /** Upper bound, inclusive. Omitted means unbounded above. */
  public readonly $max = input<number | null>(null, { alias: 'max' });

  /** Amount one press or arrow key moves the value. */
  public readonly $step = input<number>(1, { alias: 'step' });

  public readonly $disabled = input<boolean>(false, { alias: 'disabled' });

  /** Accessible name for the value, announced by the spinbutton role. */
  public readonly $label = input<string | null>(null, { alias: 'label' });

  protected readonly $atMin = computed(() => {

    const min = this.$min();
    return min !== null && this.$value() <= min;
  });

  protected readonly $atMax = computed(() => {

    const max = this.$max();
    return max !== null && this.$value() >= max;
  });

  protected readonly $decrementDisabled = computed(() => this.$disabled() || this.$atMin());

  protected readonly $incrementDisabled = computed(() => this.$disabled() || this.$atMax());

  public constructor() {

    DesignSystem.Mount(this.document, numberStepperStyleId, numberStepperBridgeStyles);
  }

  protected onStep(direction: number): void {

    if (this.$disabled()) return;

    this.commit(this.$value() + (this.$step() * direction));
  }

  protected onArrowKey(event: KeyboardEvent, direction: number): void {

    if (this.$disabled()) return;

    event.preventDefault();
    this.commit(this.$value() + (this.$step() * direction));
  }

  /** The only writer of the value — clamps, then drops a no-op rather than emitting an unchanged value. */
  private commit(next: number): void {

    const min = this.$min();
    const max = this.$max();

    let clamped = next;
    if (min !== null && clamped < min) clamped = min;
    if (max !== null && clamped > max) clamped = max;

    if (clamped === this.$value()) return;

    this.$value.set(clamped);
  }
}
